import React, { useState } from 'react';
import logo from '../logo.png';
import Overview from './admin/Overview';
import Leaderboard from './admin/Leaderboard';
import CommissionSummary from './admin/CommissionSummary';
import CommissionRates from './admin/CommissionRates';
import AdminPayouts from './admin/AdminPayouts';
import Categories from './admin/Categories';
import Items from './admin/Items';
import MyDashboard from './rep/MyDashboard';
import RepCommission from './rep/RepCommission';
import RepCommissionRate from './rep/RepCommissionRate';
import RepPayouts from './rep/RepPayouts';
import UnpaidInvoices from './rep/UnpaidInvoices';
import YearOverYear from './admin/YearOverYear';
import SalesTax from './admin/SalesTax';
import Profit from './admin/Profit';

const ADMIN_TABS = [
  { id: 'overview',           label: 'Overview' },
  { id: 'leaderboard',        label: 'Leaderboard' },
  { id: 'commission-summary', label: 'Commission summary' },
  { id: 'commission-rates',   label: 'Commission %s' },
  { id: 'payouts',            label: 'Payouts' },
  { id: 'categories',         label: 'Categories' },
  { id: 'items',              label: 'Items' },
  { id: 'sales-tax',          label: 'Sales tax' },
  { id: 'profit',             label: 'Profit' },
  { id: 'unpaid-invoices',    label: 'Unpaid invoices' },
  { id: 'year-over-year',     label: 'Year-over-year' },
];

const REP_TABS = [
  { id: 'my-dashboard',       label: 'My dashboard' },
  { id: 'rep-commission',     label: 'Commission summary' },
  { id: 'rep-commission-rate',label: 'Commission %' },
  { id: 'rep-payouts',        label: 'Payouts' },
  { id: 'sales-tax',          label: 'Sales tax' },
  { id: 'unpaid-invoices',    label: 'Unpaid invoices' },
  { id: 'year-over-year',     label: 'Year-over-year' },
];

const REP_COLORS = {
  peter: '#3ea4b1', mitch: '#7F77DD', jared: '#1D9E75', sarah: '#D4537E',
  dan: '#EF9F27', barry: '#888780', justin: '#5DCAA5', kelsey: '#F0997B',
};

function initials(name) {
  return name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
}

function LiveIndicator({ status, lastUpdated, onRefresh }) {
  const label = status === 'live'
    ? `Live · updated ${lastUpdated ? lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}`
    : status === 'loading' ? 'Connecting...'
    : 'Connection error';
  return (
    <button
      onClick={onRefresh}
      title="Click to refresh"
      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
    >
      <div className={`live-indicator ${status !== 'live' ? status : ''}`}>
        <div className="live-dot" />
        {label}
      </div>
    </button>
  );
}

export default function Dashboard({ user, data, dataStatus, lastUpdated, onLogout, onRefresh }) {
  const tabs = user.role === 'admin' ? ADMIN_TABS : REP_TABS;
  const [activeTab, setActiveTab] = useState(tabs[0].id);

  const renderPage = () => {
    if (!data) return (
      <div className="loading-screen" style={{ minHeight: 300 }}>
        <div className="spinner" />
        <span style={{ color: 'var(--text2)', fontSize: 13 }}>Loading data...</span>
      </div>
    );

    if (user.role === 'admin') {
      switch (activeTab) {
        case 'overview':           return <Overview data={data} />;
        case 'leaderboard':        return <Leaderboard data={data} />;
        case 'commission-summary': return <CommissionSummary data={data} />;
        case 'commission-rates':   return <CommissionRates data={data} />;
        case 'payouts':            return <AdminPayouts data={data} />;
        case 'categories':         return <Categories data={data} />;
        case 'items':              return <Items data={data} />;
        case 'sales-tax':          return <SalesTax data={data} />;
        case 'profit':             return <Profit />;
        case 'unpaid-invoices':    return <UnpaidInvoices user={user} isAdmin />;
        case 'year-over-year':      return <YearOverYear data={data} user={user} isAdmin />;
        default:                   return <Overview data={data} />;
      }
    } else {
      switch (activeTab) {
        case 'my-dashboard':        return <MyDashboard data={data} user={user} />;
        case 'rep-commission':      return <RepCommission data={data} user={user} />;
        case 'rep-commission-rate': return <RepCommissionRate data={data} user={user} />;
        case 'rep-payouts':         return <RepPayouts data={data} user={user} />;
        case 'unpaid-invoices':      return <UnpaidInvoices user={user} />;
        case 'year-over-year':          return <YearOverYear data={data} user={user} isAdmin={false} />;
        default:                    return <MyDashboard data={data} user={user} />;
      }
    }
  };

  const color = REP_COLORS[user.repId] || 'var(--brand)';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <div className="topbar">
        <div className="topbar-left">
          <img src={logo} className="topbar-logo" alt="JG Graphics" />
          <span className="topbar-title">JG Graphics</span>
          <span className="topbar-badge">{user.role === 'admin' ? 'Admin' : 'Sales Rep'}</span>
          <LiveIndicator status={dataStatus} lastUpdated={lastUpdated} onRefresh={onRefresh} />
        </div>
        <div className="topbar-right">
          <div className="user-pill">
            <div className="user-avatar" style={{ background: color + '22', color }}>
              {initials(user.name)}
            </div>
            <span className="user-name">{user.name}</span>
          </div>
          <button className="btn-logout" onClick={onLogout}>Sign out</button>
        </div>
      </div>

      <div className="subnav">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="main">{renderPage()}</div>
    </div>
  );
}
